import React, { useState, useEffect, useRef } from 'react';
import { Drawer, Button, List, Slider, Radio, Dropdown, DatePicker, notification } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, CalendarOutlined, PlusCircleOutlined } from '@ant-design/icons';

import L from 'leaflet';

import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';
import { getLayerImageByName } from 'assets/imgs/imageIndex';

import useLeafletLayerManager from 'hooks/useLeafletLayerManager';
import useActiveLayersStore from 'stores/useSelectedLayersStore';
import { disabledDate } from 'utils/dateFunctions';
import loadShapefile from 'utils/leafletUtils/loadShapefile';
import betterWmsLayer from 'utils/leafletUtils/betterWmsLayer';
import useSearchParamsManager from 'hooks/useSearchParamsManager';


const LayersMenu = ({ mapParameters, setMapParameters, layerRefs, mapRef, enabledDates, opacities, setOpacities }) => {

    const { createLeafletLayer, removeLeafletLayer } = useLeafletLayerManager();
    const { removeLayerUrlParam } = useSearchParamsManager();
    const selectedLayers = useActiveLayersStore((state) => state.selectedLayers);
    const getSelectedLayerInfo = useActiveLayersStore((state) => state.getSelectedLayerInfo);
    const moveSelectedLayerUp = useActiveLayersStore((state) => state.moveSelectedLayerUp);
    const moveSelectedLayerDown = useActiveLayersStore((state) => state.moveSelectedLayerDown);
    const deleteSelectedLayerByIndex = useActiveLayersStore((state) => state.deleteSelectedLayerByIndex);

    const [isOpen, setIsOpen] = useState(false);
    const [compareArray, setCompareArray] = useState([]);
    const [previousOpacities, setPreviousOpacities] = useState([]); // Store previous opacities
    const [isDisabled, setIsDisabled] = useState(false);
    const drawerHeight = Math.min(selectedLayers.length * 100 + 100, 400);

    const [comparisonLayers, setComparisonLayers] = useState([]);
    const comparisonLayerRefs = useRef([]);
    const [comparisonOpacities, setComparisonOpacities] = useState([]);

    useEffect(() => {
        return () => {
            comparisonLayerRefs.current.forEach(layer => {
                if (mapRef.current && layer) {
                    mapRef.current.removeLayer(layer);
                }
            });
        };
    }, []);

    const sideBySideControlRef = useRef(null); // Store the control reference

    useEffect(() => {
        if (sideBySideControlRef.current && mapRef.current) {
            // Remove the side-by-side comparison control if it exists
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null; // Clear the control reference
            setCompareArray([]);
        }
    }, [selectedLayers]);

    useEffect(() => {
        if (compareArray.length === 2) {
            let leftLayer, rightLayer;

            compareArray.forEach((item, idx) => {
                let layer;

                if (typeof item === 'string' && item.startsWith('comp-')) {
                    // comparison layer... extract index
                    const compIndex = parseInt(item.split('-')[1], 10);
                    layer = comparisonLayerRefs.current[compIndex];
                } else {
                    // regular layer
                    layer = layerRefs.current[selectedLayers.length - 1 - item];
                }

                if (idx === 0) leftLayer = layer;
                else rightLayer = layer;
            });

            if (leftLayer && rightLayer) {
                toggleSideBySide(leftLayer, rightLayer);
                setIsOpen(false);
            }
        } else if (compareArray.length < 2 && sideBySideControlRef.current) {
            // Remove comparison if less than 2 layers selected
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
        }
    }, [compareArray]);

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const handleOpenChange = (open) => {
        if (!open) {
            setIsDatePickerOpen(false); // Close DatePicker when it loses focus
        };
    };

    const handleCalendarClick = (index) => {
        setIsDatePickerOpen((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const handleNewDate = async (date, index) => {
        const originalLayer = selectedLayers[index];
        const originalMapParam = mapParameters[index];
        const layerInfo = getSelectedLayerInfo(index);

        const day = String(date.date()).padStart(2, '0');
        const month = String(date.month() + 1).padStart(2, '0');
        const year = date.year();

        const dateString = `${year}-${month}-${day}`;

        const comparisonLayer = {
            originalIndex: index,
            layerName: originalLayer,
            date: dateString,
            mapParam: { ...originalMapParam },
            ...layerInfo
        };

        const existingIndex = comparisonLayers.findIndex(
            l => l.layerName === originalLayer && l.date === dateString
        );

        // Remove the side-by-side comparison control if it exists
        sideBySideControlRef.current?.remove();
        sideBySideControlRef.current = null; // Clear the control reference
        setCompareArray([]);

        if (existingIndex !== -1) {
            notification.warning({
                message: 'Duplicate comparison',
                description: 'This layer with the same date is already in the comparison',
                duration: 3,
                style: {
                    backgroundColor: '#fff',
                    color: '#000',
                    borderRadius: '5px',
                    position: 'fixed',
                    top: 0,
                    right: 0
                },
            });
            return;
        }

        const newLayer = await createLeafletLayer(comparisonLayer.mapParam, day, month, year, 50);

        // Prepend instead of append — so new layer appears at top of the list
        setComparisonLayers(prev => [comparisonLayer, ...prev]);
        setComparisonOpacities(prev => [50, ...prev]);

        newLayer.addTo(mapRef.current);
        comparisonLayerRefs.current.unshift(newLayer); // also prepend for sync

        setIsDisabled(false);
    };


    const toggleSideBySide = (leftLayer, rightLayer) => {
        if (sideBySideControlRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
        }

        const control = L.control.sideBySide(leftLayer, rightLayer);
        control.addTo(mapRef.current);
        sideBySideControlRef.current = control;

        setTimeout(() => {
            mapRef.current.invalidateSize();
        }, 10);
    };

    useEffect(() => {
        comparisonOpacities.forEach((opacity, index) => {
            const layer = comparisonLayerRefs.current[index];
            if (layer && layer instanceof betterWmsLayer) {
                layer.setOpacity(opacity / 100);
            }
        });
    }, [comparisonOpacities]);

    const leaveDateCompare = () => {
        comparisonLayerRefs.current.forEach(layer => {
            if (mapRef.current && layer) {
                mapRef.current.removeLayer(layer);
            }
        });

        setComparisonLayers([]);
        comparisonLayerRefs.current = [];
        setComparisonOpacities([]);

        if (sideBySideControlRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
        }

        const menuContainer = document.querySelector('.menu-container');
        const datepickerContainer = document.querySelector('.date-picker-overlay');
        if (menuContainer && datepickerContainer) {
            menuContainer.classList.remove('disabled');
            datepickerContainer.classList.remove('disabled');
        }
        setIsOpen(true);
        setIsDisabled(false);
        setCompareArray([]);
    };

    const moveUp = (index) => {
        if (index === 0) return;

        const reorderedMapParams = [...mapParameters];
        const reorderedOpacities = [...opacities];
        const reorderedPreviousOpacities = [...previousOpacities];
        const reorderedLayerRefs = [...layerRefs.current];

        [reorderedMapParams[index - 1], reorderedMapParams[index]] = [reorderedMapParams[index], reorderedMapParams[index - 1]];
        [reorderedOpacities[index - 1], reorderedOpacities[index]] = [reorderedOpacities[index], reorderedOpacities[index - 1]];
        [reorderedPreviousOpacities[index - 1], reorderedPreviousOpacities[index]] = [reorderedPreviousOpacities[index], reorderedPreviousOpacities[index - 1]];
        [reorderedLayerRefs[index - 1], reorderedLayerRefs[index]] = [reorderedLayerRefs[index], reorderedLayerRefs[index - 1]];

        setMapParameters(reorderedMapParams);
        setOpacities(reorderedOpacities);
        setPreviousOpacities(reorderedPreviousOpacities);
        layerRefs.current = reorderedLayerRefs;
        moveSelectedLayerUp(index);

        if (mapRef.current) {
            reorderedLayerRefs.forEach((layer) => {
                if (mapRef.current.hasLayer(layer)) {
                    mapRef.current.removeLayer(layer);
                }
                layer.addTo(mapRef.current);
            });
        }

        if (sideBySideControlRef.current && mapRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
            setCompareArray([]);
        }

    };

    const moveDown = (index) => {
        if (index === selectedLayers.length - 1) return;
        const reorderedMapParams = [...mapParameters];
        const reorderedOpacities = [...opacities];
        const reorderedPreviousOpacities = [...previousOpacities];
        const reorderedLayerRefs = [...layerRefs.current];

        [reorderedMapParams[index], reorderedMapParams[index + 1]] = [reorderedMapParams[index + 1], reorderedMapParams[index]];
        [reorderedOpacities[index], reorderedOpacities[index + 1]] = [reorderedOpacities[index + 1], reorderedOpacities[index]];
        [reorderedPreviousOpacities[index], reorderedPreviousOpacities[index + 1]] = [reorderedPreviousOpacities[index + 1], reorderedPreviousOpacities[index]];
        [reorderedLayerRefs[index], reorderedLayerRefs[index + 1]] = [reorderedLayerRefs[index + 1], reorderedLayerRefs[index]];

        setMapParameters(reorderedMapParams);
        setOpacities(reorderedOpacities);
        setPreviousOpacities(reorderedPreviousOpacities);
        layerRefs.current = reorderedLayerRefs;
        moveSelectedLayerDown(index);

        if (mapRef.current) {
            reorderedLayerRefs.forEach((layer) => {
                if (mapRef.current.hasLayer(layer)) {
                    mapRef.current.removeLayer(layer);
                }
                layer.addTo(mapRef.current);
            });
        }

        if (sideBySideControlRef.current && mapRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
            setCompareArray([]);
        }
    };

    const updateOpacity = (index, value) => {
        const actualIndex = selectedLayers.length - 1 - index;

        const updatedOpacities = [...opacities];
        updatedOpacities[actualIndex] = value;
        setOpacities(updatedOpacities);

        // If there's a hidden layer, we keep its previous opacity.
        const updatedPreviousOpacities = updatedOpacities.map((opacity, index) => opacity == 0 ? previousOpacities[index] : opacity);
        setPreviousOpacities(updatedPreviousOpacities);

        layerRefs.current[actualIndex].setOpacity(value / 100);
    };

    const deleteLayer = (index) => {
        const deletedLayerName = selectedLayers[index];

        // Remove the original layer
        const updatedMapParams = mapParameters.filter((_, i) => i !== index);
        const updatedOpacities = opacities.filter((_, i) => i !== index);
        removeLeafletLayer(index, mapRef, layerRefs);
        removeLayerUrlParam(mapParameters[index]);
        setMapParameters(updatedMapParams);
        setOpacities(updatedOpacities);
        deleteSelectedLayerByIndex(index);

        // Keep previous opacities consistent
        const updatedPreviousOpacities = updatedOpacities.map((opacity, i) => opacity === 0 ? previousOpacities[i] : opacity);
        setPreviousOpacities(updatedPreviousOpacities);

        // Remove all related comparison layers
        const remainingComparisonLayers = [];
        const remainingComparisonOpacities = [];
        const newComparisonRefs = [];

        comparisonLayers.forEach((layer, idx) => {
            if (layer.layerName === deletedLayerName) {
                // Remove layer from map
                if (comparisonLayerRefs.current[idx]) {
                    mapRef.current.removeLayer(comparisonLayerRefs.current[idx]);
                }
            } else {
                remainingComparisonLayers.push(layer);
                remainingComparisonOpacities.push(comparisonOpacities[idx]);
                newComparisonRefs.push(comparisonLayerRefs.current[idx]);
            }
        });

        comparisonLayerRefs.current = newComparisonRefs;
        setComparisonLayers(remainingComparisonLayers);
        setComparisonOpacities(remainingComparisonOpacities);

        // Clean up compareArray (remove any index or comp-n matching the deleted layer)
        setCompareArray(prev => prev.filter(item => {
            if (typeof item === 'number') {
                return selectedLayers[item] !== deletedLayerName;
            }
            if (typeof item === 'string' && item.startsWith('comp-')) {
                const compIdx = parseInt(item.split('-')[1], 10);
                return remainingComparisonLayers[compIdx]?.layerName !== deletedLayerName;
            }
            return true;
        }));

        // Remove side-by-side control if needed
        if (sideBySideControlRef.current && mapRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
        }
    };

    const toggleVisibility = (index) => {

        const newOpacities = [...opacities];
        if (newOpacities[index] === 0) {
            newOpacities[index] = previousOpacities[index];
        } else {
            setPreviousOpacities((prev) => {
                prev[index] = newOpacities[index];
                return prev;
            });
            newOpacities[index] = 0;
        }
        setOpacities(newOpacities);

        layerRefs.current[index].setOpacity(newOpacities[index] / 100);
    };

    const handleRadioChange = (target) => {
        loadShapefile(target, mapRef);
    };

    const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        isBaseMap: true,
        rgbBorder: '#dadada98'
    });

    const cartoEN = L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        subdomains: 'abcd',
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        isBaseMap: true,
        rgbBorder: '#ff5d5d63'
    });


    const OpenTopoMap = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenTopoMap &copy; OpenStreetMap contributors',
        maxZoom: 17,
        isBaseMap: true,
        rgbBorder: '#ffb9fcc0'
    });

    const CartoDB_DarkMatter = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
        isBaseMap: true,
        rgbBorder: '#5c5c5c2d'
    });

    const CartoDB_Positron = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
        isBaseMap: true,
        rgbBorder: '#ff5d5d63'
    });


    const baseMaps = {
        "CartoDB Positron": CartoDB_Positron,
        "OSM": osm,
        "OpenTopoMap": OpenTopoMap,
        "CartoDB DarkMatter": CartoDB_DarkMatter,
        "Carto EN": cartoEN,
    };

    const changeLayer = (layerName) => {
        const map = mapRef.current;
        if (!map) return;

        // Remove the current basemap only
        map.eachLayer(layer => {
            if (layer instanceof L.TileLayer && layer.options?.isBaseMap) {
                map.removeLayer(layer);
            }
        });

        // Add the new basemap
        const nextBaseMap = baseMaps[layerName];
        if (nextBaseMap) {
            nextBaseMap.addTo(map);

            if (nextBaseMap.setZIndex) {
                nextBaseMap.setZIndex(0);
            }
            map.eachLayer(layer => {
                console.log(layer)
      // @ts-ignore
      if (layer.layerId === 'disputed-border') {
        layer.setStyle({
          color: nextBaseMap.options.rgbBorder || '#ff5d5d63'
        });
      }
    });
        }

        // Reset side-by-side state
        if (sideBySideControlRef.current) {
            sideBySideControlRef.current.remove();
            sideBySideControlRef.current = null;
            setCompareArray([]);
        }
    };


    const compareOnClick = (index, isComparisonLayer = false) => {
        let layerIndex = index;

        if (isComparisonLayer) {
            layerIndex = `comp-${index}`;
        }

        if (compareArray.includes(layerIndex)) {
            setCompareArray(prev => prev.filter(item => item !== layerIndex));
        } else {
            if (compareArray.length < 2) {
                setCompareArray(prev => [...prev, layerIndex]);
            } else {
                notification.info({
                    message: 'Maximum two layers',
                    description: 'Please deselect a layer first before selecting another one for comparison',
                    duration: 3
                });
            }
        }
    };

    const updateComparisonOpacity = (index, value) => {
        const newOpacities = [...comparisonOpacities];
        newOpacities[index] = value;
        setComparisonOpacities(newOpacities);

        if (index < comparisonLayerRefs.current.length) {
            const layer = comparisonLayerRefs.current[index];
            if (layer && layer instanceof betterWmsLayer) {
                layer.setOpacity(value / 100);
            }
        }
    };

    return (
        <>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    position: "fixed",
                    bottom: isOpen ? (300) : 0,
                    left: "47%",
                    background: "#1890ff",
                    borderRadius: "50% 50% 0 0",
                    padding: "12px",
                    cursor: "pointer",
                    zIndex: 1100,
                    transition: "bottom 0.3s ease",
                }}
            >
                <i className="fas fa-layer-group" style={{ fontSize: "24px", color: "#fff" }} />
            </div>

            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>Selected layers</span>
                        <Radio.Group
                            defaultValue="option1"
                            onChange={(e) => handleRadioChange(e.target.value)}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="none">None</Radio.Button>
                            <Radio.Button value="NUTS0">NUTS0</Radio.Button>
                            <Radio.Button value="NUTS1">NUTS1</Radio.Button>
                            <Radio.Button value="NUTS2">NUTS2</Radio.Button>
                            <Radio.Button value="NUTS3">NUTS3</Radio.Button>
                        </Radio.Group>
                        <Dropdown
                            menu={{
                                items: Object.keys(baseMaps).map(layerName => ({
                                    label: layerName,
                                    key: layerName,
                                    onClick: () => changeLayer(layerName)
                                }))
                            }}
                        >
                            <Button>Choose background map</Button>
                        </Dropdown>
                    </div>
                }
                placement="bottom"
                closable={true}
                onClose={() => setIsOpen(false)}
                open={isOpen}
                height={300}
            >
                {
                    comparisonLayers.length > 0 && (
                        <div className="comparison-layers-section" style={{ borderBottom: '1px solid #eee' }}>
                            <List
                                dataSource={comparisonLayers}
                                renderItem={(compLayer, index) => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                style={{
                                                    backgroundColor: compareArray.includes(`comp-${index}`) ? 'red' : 'white',
                                                    color: compareArray.includes(`comp-${index}`) ? 'white' : 'black',
                                                }}
                                                onClick={() => compareOnClick(index, true)}
                                                disabled={compareArray.length === 2 && !compareArray.includes(`comp-${index}`)}
                                            >
                                                {compareArray.includes(`comp-${index}`) ? 'Cancel' : 'Compare'}
                                            </Button>,
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => {
                                                    if (comparisonLayerRefs.current[index]) {
                                                        mapRef.current.removeLayer(comparisonLayerRefs.current[index]);
                                                    }

                                                    const newRefs = [...comparisonLayerRefs.current];
                                                    newRefs.splice(index, 1);
                                                    comparisonLayerRefs.current = newRefs;

                                                    setComparisonLayers(prev => prev.filter((_, i) => i !== index));
                                                    setComparisonOpacities(prev => prev.filter((_, i) => i !== index));

                                                    setCompareArray(prev => prev.filter(item => item !== `comp-${index}`));

                                                    if (comparisonLayers.length === 1) {
                                                        leaveDateCompare();
                                                    }
                                                }}
                                            />,
                                            <Button
                                                type="text"
                                                icon={comparisonOpacities[index] === 0 ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                onClick={() => {
                                                    const newOpacities = [...comparisonOpacities];
                                                    newOpacities[index] = newOpacities[index] === 0 ? 50 : 0;
                                                    setComparisonOpacities(newOpacities);
                                                }}
                                            />,
                                        ]}
                                    >
                                        {
                                            getLayerImageByName(LayersAndResolutions.layers.find(aux => aux.longName == compLayer.layerName).shortName) ? (
                                                <img
                                                    src={getLayerImageByName(LayersAndResolutions.layers.find(aux => aux.longName == compLayer.layerName).shortName)}
                                                    alt={compLayer.layerName}
                                                    style={{ width: '70px', height: '70px', marginRight: '25px', borderRadius: '50px' }}
                                                />
                                            ) : (
                                                <div id="bottom-calendar-button">
                                                    <CalendarOutlined style={{ fontSize: '24px' }} />
                                                </div>
                                            )
                                        }
                                        <div style={{ width: '100%' }}>
                                            <div>Copy of {compLayer.layerName} - <strong>{compLayer.date}</strong></div>
                                            <Slider
                                                min={0}
                                                max={100}
                                                value={comparisonOpacities[index] || 50}
                                                onChange={(value) => updateComparisonOpacity(index, value)}
                                            />
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </div>
                    )
                }
                <List
                    dataSource={[...selectedLayers].reverse()}
                    style={{ overflowX: 'hidden' }}
                    renderItem={(layer, index) => (
                        <List.Item
                            actions={[
                                <Button
                                    style={{
                                        backgroundColor: compareArray.includes(index) ? 'red' : 'white',
                                        color: compareArray.includes(index) ? 'white' : 'black',
                                    }}
                                    onClick={() => compareOnClick(index)}
                                    disabled={compareArray.length === 2 && !compareArray.includes(index)}
                                >
                                    {compareArray.includes(index) ? 'Cancel' : 'Compare'}
                                </Button>,
                                <Button
                                    type="text"
                                    icon={<ArrowDownOutlined />}
                                    onClick={() => moveUp(selectedLayers.length - 1 - index)}
                                    disabled={index === selectedLayers.length - 1 || isDisabled}
                                />,
                                <Button
                                    type="text"
                                    icon={<ArrowUpOutlined />}
                                    onClick={() => moveDown(selectedLayers.length - 1 - index)}
                                    disabled={index === 0 || isDisabled}
                                />,
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => deleteLayer(selectedLayers.length - 1 - index)}
                                    disabled={isDisabled}
                                />,
                                <Button
                                    type="text"
                                    icon={opacities[selectedLayers.length - 1 - index] === 0 ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                    onClick={() => toggleVisibility(selectedLayers.length - 1 - index)}
                                />,
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <Button
                                        icon={<i className="fas fa-calendar-alt"></i>}
                                        onClick={() => handleCalendarClick(index)}
                                        style={{
                                            position: 'relative',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                        }}
                                        disabled={layer.includes("Copy")}
                                    >
                                        <PlusCircleOutlined
                                            style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                fontSize: '16px',
                                                color: '#1890ff',
                                            }}
                                        />
                                    </Button>
                                    <DatePicker
                                        open={isDatePickerOpen[index]}
                                        onOpenChange={(open) => handleOpenChange(open, index)}
                                        onChange={(date) => handleNewDate(date, selectedLayers.length - 1 - index)}
                                        disabledDate={(current) => disabledDate(enabledDates, current)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: '-10px',
                                            visibility: 'hidden',
                                            zIndex: 10,
                                        }}
                                    />
                                </div>,
                            ]}
                        >
                            <img
                                src={getLayerImageByName(LayersAndResolutions.layers.find(aux => aux.longName == layer)?.shortName)}
                                alt="default"
                                style={{ width: '70px', height: '70px', marginRight: '25px', borderRadius: '50px' }} // Adjusts the image's size.
                            />

                            <div style={{ width: '100%' }}>
                                <div>{layer}</div>
                                <Slider
                                    min={0}
                                    max={100}
                                    value={opacities[selectedLayers.length - 1 - index]}
                                    onChange={(value) => updateOpacity(index, value)}
                                />
                            </div>

                        </List.Item>
                    )}
                />
            </Drawer>
        </>
    );

};

export default LayersMenu;
